import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Snackbar } from "@mui/material";
import { Alert } from "@material-ui/lab";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import getCurrentUserId from "../../reduxSaga/sagas/requests/currentUserId";
import DemandeAchatService from "../../services/DemandeAchatService";

const STATUTS = [
  { value: -1, label: "Tous" },
  { value: 0, label: "En attente" },
  { value: 1, label: "En cours" },
  { value: 2, label: "Acceptée" },
  { value: 3, label: "Refusée" },
];

const statutLabel = (s) => {
  switch (s) {
    case 0: return { text: "En attente", color: "#f0ad4e" };
    case 1: return { text: "En cours", color: "#5bc0de" };
    case 2: return { text: "Acceptée", color: "#5cb85c" };
    case 3: return { text: "Refusée", color: "#d9534f" };
    default: return { text: "-", color: "#aaa" };
  }
};

const DemandesAchat = () => {
  const navigate = useNavigate();
  const animatedComponents = makeAnimated();

  const [userRole, setUserRole] = useState([]);
  const [demandes, setDemandes] = useState([]);
  const [filteredDemandes, setFilteredDemandes] = useState([]);
  const [statutFilter, setStatutFilter] = useState({ value: -1, label: "Tous" });
  const [dateBegin, setDateBegin] = useState("");
  const [dateEnd, setDateEnd] = useState("");
  const [searchText, setSearchText] = useState("");
  const [openErrorAlert, setOpenErrorAlert] = useState(false);
  const [errorAlertText, setErrorAlertText] = useState("");
  const [openSuccessAlert, setOpenSuccessAlert] = useState(false);

  useEffect(() => {
    const status = localStorage.getItem("status");
    if (status) setUserRole(JSON.parse(status));
    loadDemandes();
  }, []);

  const loadDemandes = () => {
    DemandeAchatService.getAllDemandes(getCurrentUserId(), 0)
      .then((response) => {
        if (Object.keys(response.data)[0] === "success") {
          setDemandes(response.data.success);
          setFilteredDemandes(response.data.success);
        } else {
          setDemandes([]);
          setFilteredDemandes([]);
        }
      })
      .catch(() => {
        setOpenErrorAlert(true);
        setErrorAlertText("Erreur de connexion au serveur");
      });
  };

  const handleFilter = () => {
    const dateDebut = dateBegin || "any";
    const dateFin = dateEnd || "any";
    const statut = statutFilter ? statutFilter.value : -1;

    DemandeAchatService.getDemandesByFilters(getCurrentUserId(), dateDebut, dateFin, statut)
      .then((response) => {
        if (Object.keys(response.data)[0] === "success") {
          setFilteredDemandes(response.data.success);
        } else {
          setFilteredDemandes([]);
        }
      })
      .catch(() => {
        setOpenErrorAlert(true);
        setErrorAlertText("Erreur lors du filtrage");
      });
  };

  const handleReset = () => {
    setDateBegin("");
    setDateEnd("");
    setStatutFilter({ value: -1, label: "Tous" });
    setSearchText("");
    setFilteredDemandes(demandes);
  };

  const handleDelete = (id) => {
    if (!window.confirm("Voulez-vous supprimer cette demande ?")) return;
    DemandeAchatService.deleteDemande(id)
      .then(() => {
        setOpenSuccessAlert(true);
        loadDemandes();
      })
      .catch(() => {
        setOpenErrorAlert(true);
        setErrorAlertText("Erreur lors de la suppression");
      });
  };

  const searched = filteredDemandes.filter((d) =>
    searchText === "" ||
    d.reference?.toLowerCase().includes(searchText.toLowerCase()) ||
    d.demandeur?.toLowerCase().includes(searchText.toLowerCase()) ||
    d.service?.toLowerCase().includes(searchText.toLowerCase())
  );

  const canCreate =
    userRole.includes("DEMANDEUR") ||
    userRole.includes("PURCHASING_MANAGER") ||
    userRole.includes("ADMIN");

  const labelStyle = {
    fontSize: "0.8rem",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.03em",
    color: "#6c757d",
  };
  const cardStyle = { borderRadius: 8, boxShadow: "0 1px 6px rgba(0,0,0,.08)" };

  const countByStatut = (statut) =>
    demandes.filter((d) => d.statut === statut).length;

  return (
    <div className="content-wrapper" style={{ marginTop: "0" }}>
      <section className="content-header">
        <div className="container-fluid">
          <div className="d-flex flex-wrap justify-content-between align-items-center">
            <div>
              <h4 className="mb-1">
                <i className="fas fa-file-invoice mr-2"></i>Demandes d'achat
              </h4>
              <small className="text-muted">
                Créez, suivez et traitez les demandes d'achat de vos équipes
              </small>
            </div>
            {canCreate && (
              <button
                className="btn btn-dark mt-2 mt-sm-0 d-inline-flex align-items-center"
                style={{
                  borderRadius: 8,
                  padding: "8px 18px 8px 10px",
                  fontWeight: 500,
                }}
                onClick={() => navigate("/addDemandeAchat")}
              >
                <span
                  className="d-inline-flex align-items-center justify-content-center mr-2"
                  style={{
                    width: 26,
                    height: 26,
                    borderRadius: 6,
                    backgroundColor: "rgba(255,255,255,.18)",
                  }}
                >
                  <i className="fas fa-plus" style={{ fontSize: 12 }} />
                </span>
                Nouvelle demande
              </button>
            )}
          </div>
        </div>
      </section>

      <section className="content">
        <div className="container-fluid">
          {/* Résumé par statut */}
          <div className="row mt-3">
            {STATUTS.filter((s) => s.value !== -1).map((s) => {
              const info = statutLabel(s.value);
              return (
                <div className="col-md-3 col-6" key={s.value}>
                  <div
                    className="d-flex align-items-center p-3 mb-3 bg-white"
                    style={{
                      ...cardStyle,
                      borderLeft: `4px solid ${info.color}`,
                    }}
                  >
                    <div>
                      <small
                        className="text-muted text-uppercase d-block"
                        style={{ fontWeight: 600, letterSpacing: "0.03em" }}
                      >
                        {info.text}
                      </small>
                      <span style={{ fontSize: "1.4rem", fontWeight: 700 }}>
                        {countByStatut(s.value)}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Filtres */}
          <div className="card card-outline card-dark" style={cardStyle}>
            <div className="card-header">
              <h3 className="card-title">
                <i className="fas fa-filter mr-2"></i>Filtres
              </h3>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-3">
                  <div className="form-group">
                    <label style={labelStyle}>Date début</label>
                    <input
                      type="date"
                      className="form-control"
                      value={dateBegin}
                      onChange={(e) => setDateBegin(e.target.value)}
                    />
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="form-group">
                    <label style={labelStyle}>Date fin</label>
                    <input
                      type="date"
                      className="form-control"
                      value={dateEnd}
                      onChange={(e) => setDateEnd(e.target.value)}
                    />
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="form-group">
                    <label style={labelStyle}>Statut</label>
                    <Select
                      components={animatedComponents}
                      options={STATUTS}
                      value={statutFilter}
                      onChange={setStatutFilter}
                      placeholder="Tous"
                    />
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="form-group">
                    <label style={labelStyle}>Recherche</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Référence, demandeur, service..."
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="card-footer text-right">
              <button
                className="btn btn-outline-secondary mr-2"
                onClick={handleReset}
              >
                <i className="fas fa-undo mr-1" /> Réinitialiser
              </button>
              <button className="btn btn-dark" onClick={handleFilter}>
                <i className="fas fa-search mr-1" /> Filtrer
              </button>
            </div>
          </div>

          {/* Table */}
          <div className="card card-outline card-dark" style={cardStyle}>
            <div className="card-header">
              <h3 className="card-title">
                <i className="fas fa-list mr-2"></i>Liste des demandes d'achat
                <span className="badge badge-dark ml-2">{searched.length}</span>
              </h3>
            </div>
            <div className="card-body table-responsive p-0">
              <table className="table table-hover table-striped mb-0 text-nowrap">
                <thead className="thead-light">
                  <tr>
                    <th>Référence</th>
                    <th>Date</th>
                    <th>Demandeur</th>
                    <th>Service</th>
                    <th className="text-center">Nb. produits</th>
                    <th className="text-center">Statut</th>
                    <th className="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {searched.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center text-muted py-5">
                        <i className="fas fa-file-invoice fa-2x mb-2 d-block"></i>
                        Aucune demande trouvée
                        {canCreate && (
                          <div className="mt-3">
                            <button
                              className="btn btn-dark"
                              onClick={() => navigate("/addDemandeAchat")}
                            >
                              <i className="fas fa-plus mr-1" />
                              Créer une demande
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ) : (
                    searched.map((d) => {
                      const s = statutLabel(d.statut);
                      return (
                        <tr key={d.id}>
                          <td>
                            <span className="badge badge-light border">
                              {d.reference}
                            </span>
                          </td>
                          <td>
                            {d.dateCreation
                              ? new Date(d.dateCreation).toLocaleDateString("fr-FR")
                              : "-"}
                          </td>
                          <td>
                            <strong>{d.demandeur}</strong>
                          </td>
                          <td>{d.service}</td>
                          <td className="text-center">
                            {d.listeProduits?.length || 0}
                          </td>
                          <td className="text-center">
                            <span
                              style={{
                                backgroundColor: s.color,
                                color: "#fff",
                                padding: "3px 10px",
                                borderRadius: 10,
                                fontSize: 12,
                                fontWeight: 600,
                              }}
                            >
                              {s.text}
                            </span>
                          </td>
                          <td className="text-center">
                            <button
                              className="btn btn-sm btn-outline-dark mr-1"
                              style={{ width: 36 }}
                              title="Voir / Traiter"
                              onClick={() => navigate("/demandeAchatDetail/" + d.id)}
                            >
                              <i className="fas fa-eye" />
                            </button>
                            {(userRole.includes("ADMIN") ||
                              userRole.includes("PURCHASING_MANAGER")) &&
                              d.statut === 0 && (
                                <button
                                  className="btn btn-sm btn-outline-danger"
                                  style={{ width: 36 }}
                                  title="Supprimer"
                                  onClick={() => handleDelete(d.id)}
                                >
                                  <i className="fas fa-trash" />
                                </button>
                              )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      <Snackbar
        open={openErrorAlert}
        autoHideDuration={4000}
        onClose={() => setOpenErrorAlert(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={() => setOpenErrorAlert(false)} severity="error">
          {errorAlertText}
        </Alert>
      </Snackbar>

      <Snackbar
        open={openSuccessAlert}
        autoHideDuration={4000}
        onClose={() => setOpenSuccessAlert(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={() => setOpenSuccessAlert(false)} severity="success">
          Demande supprimée avec succès
        </Alert>
      </Snackbar>
    </div>
  );
};

export default DemandesAchat;
